import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  BreadcrumbNav,
  Button,
  Card,
  DataTable,
  HighlightCard,
  Skeleton,
  StatusMessage,
} from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Filters, Pagination } from '../../components/uiChrome.jsx';
import { AlertTriangle, Blocks, Check, ChevronDown, ChevronUp, Lock, Pencil } from 'lucide-react';
import { IntegrationProviderLogo, SurfaceStatus } from '../../components';
import { useDistribution, useDistributionTableState } from '../../hooks/distribution';
import { STATE_TO_BUCKET, SURFACE_KEYS } from '../../constants';
import { initialsOf, REQUESTER_AVATAR_CLASS } from '../../utils';
import SurfaceExposureModal from './SurfaceExposureModal.jsx';

const PAGE_WRAP = 'flex min-h-0 flex-1 flex-col bg-surface-base';
const PAGE_INNER = 'px-6';
const TABLE_HOVER = { row: 'hover:!bg-[#E8F1FF] dark:hover:!bg-[#152A45]', td: '!h-auto py-3 align-middle', th: '!h-12' };

// Health bucket ordering for the Status sort (working → needs attention → other),
// mirroring the prototype's name/status/owner sortable columns.
const STATUS_RANK = { working: 0, needs_attention: 1, disconnected: 2 };

/**
 * Clickable column header for the sortable columns (name / status / owner).
 * `DataTable` renders `col.label` straight into the `<th>` and ignores clicks
 * on `button` for row selection, so a header button sorts without touching the
 * shared component.
 */
const SortHeader = ({ label, active, dir, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group inline-flex items-center gap-1 text-inherit"
    aria-label={label}
  >
    {label}
    <span className={active ? 'opacity-100' : 'opacity-30 transition-opacity group-hover:opacity-60'}>
      {active && dir === 'desc' ? (
        <ChevronDown size={14} aria-hidden="true" />
      ) : (
        <ChevronUp size={14} aria-hidden="true" />
      )}
    </span>
  </button>
);

const HealthCell = ({ status, t }) => {
  const bucket = STATE_TO_BUCKET[status] ?? 'disconnected';
  if (bucket === 'working') {
    return (
      <Badge variant="success" size="medium" icon={Check}>
        {t('integrations.distribution.health.working')}
      </Badge>
    );
  }
  if (status === 'auth_failed') {
    return (
      <Badge variant="warning" size="medium" icon={AlertTriangle}>
        {t('integrations.status.auth_failed')}
      </Badge>
    );
  }
  return (
    <Badge variant="warning" size="medium" icon={AlertTriangle}>
      {t('integrations.distribution.health.needsAttention')}
    </Badge>
  );
};

/**
 * One surface cell: a read-only three-state badge, or — in manage mode, for a
 * supported (non-N/A) surface — an editable button that opens the governed
 * change modal (CT-4207).
 */
const EditableSurfaceCell = ({ row, surfaceKey, manage, t, onEdit }) => {
  const value = row.surfaces?.[surfaceKey];
  if (!manage || value == null) return <SurfaceStatus value={value} t={t} />;
  return (
    <button
      type="button"
      onClick={() => onEdit(row, surfaceKey, Boolean(value))}
      className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 transition-colors hover:bg-surface-hover"
      aria-label={t('integrations.distribution.change.edit', {
        surface: t(`integrations.distribution.columns.${surfaceKey}`),
      })}
    >
      <SurfaceStatus value={value} t={t} />
      <Pencil size={12} className="text-content-faint" aria-hidden="true" />
    </button>
  );
};

/**
 * Distribution table (CT-4206 + CT-4207, admin-only). Full-width (`px-6` = 1.5rem)
 * master view of where each connected integration is exposed — per surface, with
 * health and owner. Sortable by name/status/owner; summary tiles are
 * `HighlightCard`. A row click opens the integration detail (prototype parity);
 * "Manage exposure" (CT-4207) unlocks inline per-surface toggles — each change
 * goes through a governed modal (reason + audit) via `SurfaceExposureModal`.
 */
const DistributionPage = () => {
  const { t } = useTranslation('admin-studio');
  const navigate = useNavigate();
  const {
    search,
    setSearch,
    status,
    setStatus,
    sortKey,
    sortDir,
    toggleSort,
    page,
    setPage,
    perPage,
    setPerPage,
    resetPage,
  } = useDistributionTableState();
  const [manage, setManage] = useState(false);
  const [change, setChange] = useState(null); // { slug, name, surface, currentlyOn }
  const { rows, stats, isLoading, error, reload } = useDistribution(search, status);

  // Sort by name / status / owner (prototype-parity). Status orders by health
  // bucket; name/owner are locale-aware string compares. `sortDir` flips the
  // direction; the surface columns are not sortable.
  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const valueOf = (row) => {
      if (sortKey === 'status') return STATUS_RANK[STATE_TO_BUCKET[row.status] ?? 'disconnected'] ?? 99;
      if (sortKey === 'owner') return (row.owner ?? '').toLowerCase();
      return (row.name ?? '').toLowerCase();
    };
    return [...rows].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / perPage));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * perPage;
  const pageRows = useMemo(
    () => sortedRows.slice(pageStart, pageStart + perPage),
    [sortedRows, pageStart, perPage],
  );
  const rangeLabel =
    sortedRows.length === 0 ? '0' : `${pageStart + 1}–${Math.min(pageStart + perPage, sortedRows.length)}`;

  const openChange = useCallback((row, surface, currentlyOn) => {
    setChange({ slug: row.slug, name: row.name, surface, currentlyOn });
  }, []);

  // Plain objects — the React Compiler memoizes them; a manual useMemo here
  // can't be preserved once the setters come from a custom hook
  // (react-hooks/preserve-manual-memoization).
  const searchProps = {
    value: search,
    onChange: (e) => resetPage(() => setSearch(e?.target ? e.target.value : e)),
    onClear: () => resetPage(() => setSearch('')),
    placeholder: t('integrations.distribution.searchPlaceholder'),
  };

  const filtersConfig = [
    {
      id: 'status',
      label: t('integrations.distribution.statusFilter'),
      multiple: false,
      value: status === 'all' ? [] : [status],
      onChange: (next) => resetPage(() => setStatus(next[0] ?? 'all')),
      options: [
        { value: 'working', label: t('integrations.distribution.health.working') },
        { value: 'needs_attention', label: t('integrations.distribution.health.needsAttention') },
      ],
    },
  ];

  const columns = useMemo(
    () => [
      {
        key: 'name',
        label: (
          <SortHeader
            label={t('integrations.distribution.columns.integration')}
            active={sortKey === 'name'}
            dir={sortDir}
            onClick={() => toggleSort('name')}
          />
        ),
        render: (v, row) => (
          <span className="flex min-w-0 items-center gap-2.5">
            <IntegrationProviderLogo slug={row.slug} name={row.name} size="small" />
            <span className="min-w-0">
              <span className="block truncate text-[13px] font-semibold text-content">{v}</span>
              <span className="block truncate text-[12px] text-content-faint">{row.vendor}</span>
            </span>
          </span>
        ),
      },
      {
        key: 'status',
        label: (
          <SortHeader
            label={t('integrations.distribution.columns.status')}
            active={sortKey === 'status'}
            dir={sortDir}
            onClick={() => toggleSort('status')}
          />
        ),
        headerClassName: 'w-[148px]',
        render: (value) => <HealthCell status={value} t={t} />,
      },
      {
        key: 'owner',
        label: (
          <SortHeader
            label={t('integrations.distribution.columns.owner')}
            active={sortKey === 'owner'}
            dir={sortDir}
            onClick={() => toggleSort('owner')}
          />
        ),
        headerClassName: 'w-[180px]',
        render: (value) =>
          value ? (
            <span className="flex min-w-0 items-center gap-2">
              <Avatar alt={value} initials={initialsOf(value)} className={REQUESTER_AVATAR_CLASS} />
              <span className="truncate text-[13px] text-content">{value}</span>
            </span>
          ) : (
            <span className="text-content-faint">—</span>
          ),
      },
      ...SURFACE_KEYS.map((key) => ({
        key,
        label: t(`integrations.distribution.columns.${key}`),
        render: (_v, row) => (
          <EditableSurfaceCell row={row} surfaceKey={key} manage={manage} t={t} onEdit={openChange} />
        ),
      })),
    ],
    [t, sortKey, sortDir, toggleSort, manage, openChange],
  );

  const pager = (
    <Pagination
      className="shrink-0"
      rangeLabel={rangeLabel}
      totalItems={rows.length}
      currentPage={safePage}
      totalPages={totalPages}
      perPage={perPage}
      hasPrevious={safePage > 1}
      hasNext={safePage < totalPages}
      onPrevPage={() => setPage(Math.max(1, safePage - 1))}
      onNextPage={() => setPage(Math.min(totalPages, safePage + 1))}
      onPageSizeChange={(size) => {
        setPerPage(size);
        setPage(1);
      }}
    />
  );

  return (
    <main className={PAGE_WRAP}>
      <div className={`${PAGE_INNER} flex-1 overflow-y-auto pb-4 pt-3.5 scrollbar-aimsos-auto-sm`}>
        <BreadcrumbNav
          size="sm"
          items={[
            { label: t('integrations.home.title'), path: '/admin-studio/integrations' },
            { label: t('integrations.distribution.nav'), path: '.' },
          ]}
          onNavigate={(path) => navigate(path)}
        />

        <div className="mb-4 mt-4 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-[-0.015em] text-content">
              {t('integrations.distribution.title')}
            </h1>
            <p className="mt-1 text-[13px] text-content-secondary">{t('integrations.distribution.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-stretch gap-2">
            <HighlightCard
              variant="purple"
              value={stats.connected}
              feedback={t('integrations.distribution.stats.connected')}
              showIcon={false}
              showUnit={false}
              className="!w-auto min-w-[8.5rem] !p-3"
            />
            <HighlightCard
              variant="green"
              value={stats.exposedAll}
              feedback={t('integrations.distribution.stats.exposedAll')}
              showIcon={false}
              showUnit={false}
              className="!w-auto min-w-[8.5rem] !p-3"
            />
            <HighlightCard
              variant="orange"
              value={stats.unexposed}
              feedback={t('integrations.distribution.stats.unexposed')}
              showIcon={false}
              showUnit={false}
              className="!w-auto min-w-[8.5rem] !p-3"
            />
          </div>
        </div>

        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : error ? (
          <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <Filters
                  searchProps={searchProps}
                  filters={filtersConfig}
                  showAllFilters={false}
                  maxFilters={1}
                  onClearAll={() => {
                    setSearch('');
                    setStatus('all');
                    setPage(1);
                  }}
                />
              </div>
              <Button
                variant={manage ? 'primary' : 'secondary'}
                size="md"
                leftIcon={manage ? Check : Lock}
                onClick={() => setManage((m) => !m)}
              >
                {t(manage ? 'integrations.distribution.manageDone' : 'integrations.distribution.manageExposure')}
              </Button>
            </div>

            {manage ? (
              <p className="text-xs text-content-muted">{t('integrations.distribution.manageNote')}</p>
            ) : null}

            {rows.length === 0 ? (
              <StatusMessage
                icon={Blocks}
                title={
                  stats.connected === 0
                    ? t('integrations.distribution.emptyTitle')
                    : t('integrations.distribution.noResults')
                }
              />
            ) : (
              <Card className="overflow-hidden">
                <DataTable
                  variant="workspace"
                  columns={columns}
                  data={pageRows}
                  minTableWidth={1100}
                  emptyMessage={t('integrations.distribution.noResults')}
                  onRowClick={(row) => navigate(`/admin-studio/integrations/${row.slug}`)}
                  classNames={TABLE_HOVER}
                />
              </Card>
            )}
          </div>
        )}

        {change ? (
          <SurfaceExposureModal
            change={change}
            onClose={() => setChange(null)}
            onApplied={() => {
              setChange(null);
              reload();
            }}
          />
        ) : null}
      </div>

      {!isLoading && !error ? <div className={`${PAGE_INNER} shrink-0 py-3`}>{pager}</div> : null}
    </main>
  );
};

export default DistributionPage;
